import { useNavigate } from "react-router-dom";
import { useCases, type Case } from "../hooks/useCases";
import { isSupabaseConfigured } from "../lib/supabase";

/**
 * Active campaigns — the user's case list.
 *
 * Renders one of five mutually exclusive states, each brand-tonal:
 *   1. not-configured  → Supabase env not filled in yet (early dev)
 *   2. loading         → query in flight
 *   3. error           → query failed (network, auth, RLS rejection)
 *   4. empty           → no rows visible to this user
 *   5. has-cases       → list of case cards
 */
function CasesList() {
  const { data, isLoading, error, refetch, isFetching } = useCases();

  if (!isSupabaseConfigured()) {
    return <NotConfigured />;
  }

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return <ErrorState message={error.message} onRetry={() => refetch()} />;
  }

  if (!data || data.length === 0) {
    return <EmptyState />;
  }

  return (
    <CasesGrid cases={data} stale={isFetching} onRefresh={() => refetch()} />
  );
}

function SectionFrame({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="w-full">
      <div className="mb-4 flex items-baseline justify-between">
        <p className="font-display text-lg font-light uppercase tracking-[0.18em] text-bone-mute">
          {label}
        </p>
        <span className="font-display text-lg font-light uppercase tracking-[0.12em] text-bone-mute/60">
          // war room
        </span>
      </div>
      {children}
    </div>
  );
}

function NotConfigured() {
  const navigate = useNavigate();
  return (
    <SectionFrame label="command surface // offline">
      <div className="rounded border border-steel-dark bg-iron/70 px-6 py-8 backdrop-blur-sm">
        <p className="font-display text-2xl font-light text-bone">
          The archives are not yet connected.
        </p>
        <p className="mt-3 font-display text-lg text-bone-dim">
          The berserkr-app Supabase project has not been wired in yet. Once
          the project exists, fill in{" "}
          <code className="font-display text-bone">VITE_SUPABASE_URL</code> and{" "}
          <code className="font-display text-bone">VITE_SUPABASE_ANON_KEY</code>{" "}
          in <code className="font-display text-bone">.env.local</code> and
          restart the dev server.
        </p>
        <p className="mt-3 font-display text-lg font-light uppercase tracking-[0.12em] text-bone-mute/70">
          // standing by
        </p>
        <button
          type="button"
          onClick={() => navigate("/case/preview")}
          className="mt-6 border border-ember-bright/60 bg-ember/20 px-5 py-2 font-display text-lg font-light uppercase tracking-[0.14em] text-bone transition-colors hover:bg-ember/40 hover:shadow-ember-soft"
        >
          Preview the war room →
        </button>
      </div>
    </SectionFrame>
  );
}

function LoadingState() {
  return (
    <SectionFrame label="active campaigns">
      <div className="rounded border border-steel-dark bg-iron/70 px-6 py-8 backdrop-blur-sm">
        <p className="flex items-center gap-3 font-display text-lg font-light uppercase tracking-[0.16em] text-bone-mute">
          <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow" />
          <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:200ms]" />
          <span className="inline-block h-1.5 w-1.5 animate-pulse-ember rounded-full bg-ember-bright shadow-ember-glow [animation-delay:400ms]" />
          consulting the archives
        </p>
      </div>
    </SectionFrame>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <SectionFrame label="the lines are cut">
      <div className="rounded border border-ember-bright/40 bg-ember/20 px-6 py-6 backdrop-blur-sm">
        <p className="font-display text-xl font-light text-ember-bright">
          The archives did not answer.
        </p>
        <p className="mt-2 font-display text-lg text-bone-dim">{message}</p>
        <button
          onClick={onRetry}
          className="mt-4 border border-ember-bright/60 bg-black/40 px-4 py-2 font-display text-lg font-light uppercase tracking-[0.14em] text-bone transition-colors hover:bg-ember/40 hover:shadow-ember-soft"
        >
          Try again →
        </button>
      </div>
    </SectionFrame>
  );
}

function EmptyState() {
  return (
    <SectionFrame label="active campaigns">
      <div className="rounded border border-steel-dark bg-iron/70 px-6 py-8 backdrop-blur-sm">
        <p className="font-display text-2xl font-light text-bone">
          No campaigns underway.
        </p>
        <p className="mt-3 font-display text-lg text-bone-dim">
          When a case is provisioned for your firm or shared under an
          agreement you're party to, it will appear here.
        </p>
      </div>
    </SectionFrame>
  );
}

function CasesGrid({
  cases,
  stale,
  onRefresh,
}: {
  cases: Case[];
  stale: boolean;
  onRefresh: () => void;
}) {
  return (
    <SectionFrame
      label={`active campaigns // ${cases.length} ${cases.length === 1 ? "matter" : "matters"}`}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {cases.map((c) => (
          <CaseCard key={c.id} caseRow={c} />
        ))}
      </div>
      <button
        onClick={onRefresh}
        disabled={stale}
        className="mt-4 font-display text-lg font-light uppercase tracking-[0.14em] text-bone-mute hover:text-bone disabled:opacity-50"
      >
        {stale ? "refreshing…" : "refresh ↻"}
      </button>
    </SectionFrame>
  );
}

function CaseCard({ caseRow }: { caseRow: Case }) {
  const navigate = useNavigate();
  return (
    <button
      type="button"
      onClick={() => navigate(`/case/${caseRow.id}`)}
      className="group rounded border border-steel-dark bg-iron/70 px-5 py-4 text-left backdrop-blur-sm transition-colors hover:border-ember-bright/50 hover:bg-iron-2/80"
    >
      <div className="flex items-center justify-between">
        <span className="font-display text-lg font-light uppercase tracking-[0.12em] text-bone-mute">
          {caseRow.case_number}
        </span>
        <StatusPill status={caseRow.status} />
      </div>
      <p className="mt-2 font-display text-lg font-light leading-tight text-bone group-hover:text-ember-bright">
        {caseRow.caption}
      </p>
      <p className="mt-2 font-display text-lg font-light uppercase tracking-[0.1em] text-bone-mute/80">
        {caseRow.court} // {caseRow.jurisdiction} // filed{" "}
        {formatDate(caseRow.filed_at)}
      </p>
    </button>
  );
}

function StatusPill({ status }: { status: Case["status"] }) {
  const styles: Record<Case["status"], string> = {
    active: "border-ember-bright/60 text-ember-bright",
    stayed: "border-bone-mute/60 text-bone-mute",
    closed: "border-bone-mute/40 text-bone-mute/70",
    dismissed: "border-bone-mute/40 text-bone-mute/70",
    on_appeal: "border-blue-bright/60 text-blue-bright",
  };
  return (
    <span
      className={`rounded border px-2 py-0.5 font-display text-base font-light uppercase tracking-[0.1em] ${styles[status]}`}
    >
      {status.replace("_", " ")}
    </span>
  );
}

function formatDate(iso: string): string {
  // YYYY-MM-DD → 23 May 2026
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

export default CasesList;
